import { BusEvent, HpStep, ProjectStatus, SalesPackageType } from '@/helper/Types'
import projectStore from '@/store/ProjectStore'
import appStore from '@/store/AppStore'
import i18n from '@/helper/Translations'
import router from '@/helper/router'
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import { tagStepView } from '@/helper/analytics'
import FormController from '@/controllers/FormController'
import ProjectController from '@/controllers/ProjectController'
import formStore from '@/store/FormStore'
import { setMetaTitle } from '@/helper/tools'
import { psIntegration } from '@/services/PsIntegrationService.ts'
import userStore from '@/store/UserStore.ts'

export class StepsController {
  public static initListeners(): void {
    const COMPONENT_NAME = 'StepsController'

    bus.$offC(COMPONENT_NAME)

    bus.$onC(COMPONENT_NAME, BusEvent.FORCE_GO_TO_STEP, async (payload: HpStep) => {
      await this.goToStep(payload)
    })

    bus.$onC(COMPONENT_NAME, BusEvent.LOADING_FINISHED, () => {
      if (!appStore.currentStepDefaultNextValidity) {
        setTimeout(this.currentStepValid, 1)
      }
    })

    bus.$onC(COMPONENT_NAME, BusEvent.LANGUAGE_CHANGED, () => {
      StepsController.setCurrentStepTitle()
    })
  }

  public static async init() {
    appStore.currentStep = HpStep.EMPTY
    appStore.resetSteps()
    projectStore.resetProject()
    appStore.resetSteps()
    appStore.isLoading = true

    projectStore.projectId =
      (router.currentRoute.value.params.projectId || 'new') === 'new'
        ? ''
        : router.currentRoute.value.params.projectId.toString()

    this.initListeners()

    if (!projectStore.projectId) {
      await this.goToFirstStep()
      appStore.isLoading = false
      setTimeout(() => bus.$emit(BusEvent.LOAD_PROJECT_FINISHED), 100)
    } else {
      //Edit project
      try {
        await ProjectController.loadProject()

        if (await this.checkUpdateRequired()) {
          if (userStore.planningSuiteToken && projectStore.status === ProjectStatus.UPDATE_REQUIRED) {
            const projectId = projectStore.projectId
            const updateProducts = await ProjectController.getProjectUpdateProducts(projectId)

            bus.$emit(BusEvent.SHOW_MODAL, {
              modalType: ModalTypes.PS_UPDATE_PROJECT,
              confirmEventName: BusEvent.UPDATE_PROJECT_CONFIRMED,
              payload: ({ updateProducts, updateConfirmed: false, projectId })
            })
          } else {
            return
          }
        }

        // goto first invalid step
        await this.goToStep(this.validateSteps())
        psIntegration.signalReady()

        bus.$emit(BusEvent.LOAD_PROJECT_FINISHED)
      } catch (e) {
        appStore.errorLoadingProject = true
        window.console.error('Error load project in StepsController', e)

        //it will be discussed how should behave in future
        if (userStore.planningSuiteToken) {
          psIntegration.closeIntegration()
          return
        }

        await router.replace({ name: 'ProjectOverviewPage' })
      } finally {
        appStore.isLoading = false
      }
    }
  }

  private static get phasedOutReview() {
    return window.history.state.phasedOutReview
  }

  private static async checkUpdateRequired(): Promise<boolean> {
    if (projectStore.status !== ProjectStatus.UPDATE_REQUIRED) {
      return false
    }

    if (!this.phasedOutReview) {
      if (userStore.planningSuiteToken) {
        await router.push({
          name: 'project',
          params: { id: projectStore.projectId }
        })
      } else {
        await router.replace({ name: 'ProjectOverviewPage', state: { updateProject: projectStore.projectId } })
      }

      return true
    }

    return false
  }

  private static async goToFirstStep() {
    await this.goToStep(appStore.firstStep)
  }

  private static async goToStep(step: HpStep) {
    if (step === appStore.currentStep) {
      return
    }

    if (step === HpStep.EMPTY) {
      //it will be discussed how should behave in future
      if (userStore.planningSuiteToken) {
        psIntegration.closeIntegration()
        return
      }

      projectStore.resetProject()
      appStore.resetSteps()
      setMetaTitle(i18n.global.t('overview.title').toString())
      await router.replace({ name: 'ProjectOverviewPage' })
      return
    }

    appStore.currentStep = step
    StepsController.setCurrentStepTitle()
    FormController.loadFormData(step)
    tagStepView()

    window.setTimeout(() => {
      bus.$emit(BusEvent.STEP_CHANGED)
      bus.$emit(BusEvent.FOCUS_FIRST_ELEMENT)
    }, 0)

    scrollTo({ top: 0 })
  }

  public static setCurrentStepTitle(): void {
    setMetaTitle(i18n.global.t(appStore.getStepModelByHpStep(appStore.currentStep).name).toString())
  }

  /**
   * Validates steps when loading project to show valid steps and jump to last invalid step
   * @private
   */
  private static validateSteps(): HpStep {
    for (const step of appStore.steps) {
      if (projectStore.stepContainsPhasedOutProduct(step.step)) {
        return step.step
      }

      if (step.step === HpStep.ACCESSORIES) {
        const selectedAccessories = Object.keys(projectStore.projectProperties.selectedAccessories.val || {})
          .filter(accessoryId => accessoryId !== projectStore.projectProperties.systemControllerId.val)

        if (selectedAccessories.length) {
          appStore.setStepValidity(step.step, true)
          continue
        }

        if (projectStore.projectProperties.explicitlyNoAccessoriesSelected.val) {
          appStore.setStepValidity(step.step, true)
        } else {
          appStore.setStepValidity(step.step, false)
          return step.step
        }
      }

      // if step = outphasedStep Return step
      if (!ProjectController.validateStep(step.step)) {
        return step.step
      } else {
        appStore.setStepValidity(step.step, true)
      }
    }
    return HpStep.SUMMARY
  }

  public static async currentStepValid(scrollToError = true): Promise<boolean> {
    if (
      appStore.currentStepComponent &&
      'validate' in appStore.currentStepComponent &&
      !await appStore.currentStepComponent.validate()
    ) {
      if (scrollToError) {
        bus.$emit(BusEvent.SCROLL_TO_FIRST_ERROR)
      }

      appStore.setCurrentStepValidity(false)
      appStore.currentFormValid = false
      return false
    }
    appStore.currentFormValid = true
    return true
  }

  /**
   * On next button in Steps controls click
   */
  public static async nextClicked() {
    if (!appStore.currentStepComponent) {
      return
    }

    if (appStore.currentStep === this.lastHpStep) {
      appStore.setCurrentStepValidity(true)
      await router.push({ name: 'DownloadPage', params: { projectId: projectStore.projectId } })

      return
    }

    if (!await this.currentStepValid()) {
      return
    }

    appStore.isLoading = true
    appStore.setCurrentStepValidity(true)

    try {
      if (FormController.hasUnsavedChanges() || formStore.forceSave || this.phasedOutReview) {
        await ProjectController.saveProject()
      }

      formStore.forceSave = false
      if (await this.checkUpdateRequired()) {
        return
      }

      if ((router.currentRoute.value.params.projectId || '') === 'new') {
        await router.replace({ name: 'project', params: { projectId: projectStore.projectId } })
      }
      await this.goToStep(appStore.nextStep)
    } catch (e) {
      appStore.setCurrentStepValidity(false)
      window.console.error('StepController error in save project')
      throw e
    } finally {
      appStore.isLoading = false
    }
  }

  /**
   * Returns the last step from steps sequence as HpStep
   * @private
   */
  private static get lastHpStep(): HpStep {
    return appStore.steps[appStore.steps.length - 1].step
  }

  /**
   * On prev button in Steps Controls click
   */
  public static async backClicked() {
    await this.jumpToStep(appStore.prevStep)
  }

  public static async jumpToStep(step: HpStep) {
    if (HpStep.EMPTY === step) {
      return
    }

    if (step === appStore.currentStep) {
      return
    }

    if (
      step !== appStore.firstStep &&
      !(appStore.prevStepModelByStep(step) && appStore.prevStepModelByStep(step).isValid) &&
      !appStore.getStepModelByHpStep(step).isValid
    ) {
      return
    }

    if (FormController.hasUnsavedChanges()) {
      if (projectStore.projectProperties.salePackage.val && step === HpStep.COMPONENTS) {
        if (projectStore.projectProperties.salePackage.val === SalesPackageType.SALES_PACKAGE && !projectStore.projectProperties.salePackageId.val) {
          appStore.setStepCondition('includeSaleSet', true)
          bus.$emit(BusEvent.SHOW_MODAL, {
            modalType: ModalTypes.UNSAVED_CHANGES,
            confirmEventName: BusEvent.FORCE_GO_TO_STEP,
            payload: HpStep.SALE_SET
          })
          return
        }
        appStore.setStepCondition('includeSaleSet', true)

        bus.$emit(BusEvent.SHOW_MODAL, {
          modalType: ModalTypes.UNSAVED_CHANGES,
          confirmEventName: BusEvent.FORCE_GO_TO_STEP,
          payload: step
        })
        return
      }

      if (!projectStore.projectProperties.salePackage.val && step === HpStep.SALE_SET) {
        appStore.setStepCondition('includeSaleSet', false)

        bus.$emit(BusEvent.SHOW_MODAL, {
          modalType: ModalTypes.UNSAVED_CHANGES,
          confirmEventName: BusEvent.FORCE_GO_TO_STEP,
          payload: HpStep.COMPONENTS
        })
        return
      }

      if (projectStore.projectProperties.salePackage.val) {
        appStore.setStepCondition('includeSaleSet', true)
      } else {
        appStore.setStepCondition('includeSaleSet', false)
      }

      bus.$emit(BusEvent.SHOW_MODAL, {
        modalType: ModalTypes.UNSAVED_CHANGES,
        confirmEventName: BusEvent.FORCE_GO_TO_STEP,
        payload: step
      })
      return
    }

    await this.goToStep(step)
  }
}
